import React, { useEffect, useState, useRef, useMemo } from "react";
import { throttle } from "lodash";
import axios from "axios";
import urls from "../../../utils/url";
import { useParams, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import Loader from "../../../components/common/loader";
import "./chartLayout2.scss";
import { getReadableTimeOnly, isJson, getDisplayName, isValidPatientName, isRealPatientId, iconMaskStyle } from "../../../utils/converter";
import { useTabVisible } from "../../../hooks/useTabVisible";
import Graph_new from "../../../components/graphs/graph_new";
import Cookie from "js-cookie";
import Tooltip from "@mui/material/Tooltip";

import {
  saveToIndexedDB,
  loadFromIndexedDB,
  cleanupOldIndexedDB,
  deleteFromIndexedDB,
  saveFetalMarkers,
  loadFetalMarkers,
  deleteFetalMarkers,
} from "../../../utils/IndexedDB";

// Owns the critical-alert pulse animation (severity state + its 1400ms
// interval) in its own tiny component, isolated from ChartLayout2's body.
// Previously this state lived inside ChartLayout2 itself, so every pulse
// tick re-ran the ENTIRE component function — including the full vitals
// sort/transform over the whole retained time series — purely to toggle a
// CSS class. Since `children` here is the same React element reference
// across those ticks (ChartLayout2 itself didn't re-render), React bails
// out of reconciling the chart subtree entirely when only this wrapper's
// own state changes. No visual/class behavior changes.
const CriticalPulseWrapper = ({ isCritical, isSingleView, theme, children }) => {
  const [severity, setSeverity] = useState("normal");
  const pulseRef = useRef(null);

  useEffect(() => {
    if (isCritical) {
      if (!pulseRef.current) {
        setSeverity("critical");

        pulseRef.current = setInterval(() => {
          setSeverity((prev) => (prev === "critical" ? "warning" : "critical"));
        }, 1400);
      }
    } else {
      if (pulseRef.current) {
        clearInterval(pulseRef.current);
        pulseRef.current = null;
      }

      setSeverity("warning");
      const t = setTimeout(() => {
        setSeverity("normal");
      }, 800);

      return () => clearTimeout(t);
    }

    return () => {
      if (pulseRef.current && !isCritical) {
        clearInterval(pulseRef.current);
        pulseRef.current = null;
      }
    };
  }, [isCritical]);

  return (
    <div
      className={`monitor-card-scale
         ${theme === "light-theme" ? "light-theme" : ""}
  ${!isSingleView && severity === "critical" ? "is-critical" : ""}
    ${!isSingleView && severity === "warning" ? "is-warning" : ""}
    ${isSingleView ? "layout-single" : "layout-grid"}
     `}
    >
      {children}
    </div>
  );
};

const ChartLayout2 = ({
  keyar_data,
  session_time,
  session_uuid,
  patientBasicDetail,
  patientID,
  viewMode = "grid",
  isBeingCleaned = false,
  isCritical,
  onAnimatedDataUpdate,
  onCriticalChange,
    hospitalName,
  hospitalLogo,
}) => {
  const cookie = Cookie.get("access_token");
  const params = useParams();
  const navigate = useNavigate();
  const [keyarData, setKeyarData] = useState({});
  const [loading, setLoading] = useState(false);
  const [patientDetail, setPatientDetail] = useState({});
  const [startTime, setStartTime] = useState("");
  const [fhrcritical, setfhrCritical] = useState(false);
  const [fhr2critical, setfhr2Critical] = useState(false);
  const [mhrcritical, setmhrCritical] = useState(false);
  const [keyar, setKeyar] = useState({});
  const [isDelayedByOneMin, setIsDelayedByOneMin] = useState(false);
  const [deviceID, setDeviceID] = useState("");
  const [sessionTime, setSessionTime] = useState("");
  const [accountType, setAccountType] = useState("");
  const [interpretationsDisable, setInterpretationsDisable] = useState("");
  const [theme, setTheme] = useState();
  const [cacheReady, setCacheReady] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const VITAL_LABEL_COLORS = {
    fhr1: theme === "light-theme" ? "#1C1F89" : "#0393D9",
    fhr2: theme === "light-theme" ? "#841717" : "#FF6905",
    mhr: "#E13D92",
    spo2: "#00F5FF",
    toco: "#F59E0B",
  };

  const isMountedRef = useRef(true);
  const currentPatientRef = useRef(patientID);
  const animationIntervalRef = useRef(null);
  const historyFetchedRef = useRef(false);
  const abortControllerRef = useRef(null);
  const cacheLoadedRef = useRef(false);
  const saveDebounceRef = useRef(null);
  const sessionEndTimeoutRef = useRef(null);
  const cacheCheckedRef = useRef(false);
  const isSingleView = viewMode === "single";
  const isNewSessionRef = useRef(false);
  const sessionBlockedRef = useRef(false);
  const fetalMarkersRef = useRef({
    manual: {},
    automated: {},
  });

  // WebSocket-sourced patients (liveKeyarData slice tags its params
  // source:"ws") plot straight from keyar_data's latest_data: no replay
  // timer, and never waiting on IndexedDB. REST-sourced patients keep the
  // original replay pipeline below, untouched.
  const isWsLive = !!keyar_data?.some((p) => p.source === "ws");
  const wsHistoryPatientRef = useRef(null);
  const wsLastUuidRef = useRef(null);
  const wsLastNewestRef = useRef({ ts: 0, at: Date.now() });
  const keyarDataPropRef = useRef(keyar_data);
  const isTabVisible = useTabVisible();
  const seriesCacheRef = useRef({ deps: null, value: null, builtAt: 0 });
  keyarDataPropRef.current = keyar_data;
  const wsPlottedLastRef = useRef(null);
  const keyarDataRef = useRef(null);
  keyarDataRef.current = keyarData;
  const wsStallLoggedAtRef = useRef(0);

  const timestampsPerUpdate = 1;
  const updateInterval = 250;

  const cookieUserData = Cookie.get("data");
  const cookieData = Cookie.get("theme");

  const lastAnimatedTimestampRef = useRef({});
  const sortedDataRef = useRef({});
  const sessionAnchorRef = useRef(null);
  const isValidNumber = (v) => typeof v === "number" && !isNaN(v) && v !== 0;

  const fullDatasetRef = useRef({});
  const lastNewDataTimeRef = useRef({});
  const lastAlertsSentRef = useRef({ timestamp: 0, alerts: [] });
  const RETENTION_WINDOW = 1800;

  const MAX_HOLD = {
    mhr: 2,
    uc: 3,
    fhr2: 2,
    spo2: 3,
  };

  const getPatientIdentifier = () => {
    if (
      patientDetail?.mrn &&
      patientDetail.mrn !== "NA" &&
      patientDetail.mrn !== "-" &&
      patientDetail.mrn.trim() !== ""
    ) {
      return { label: "MRN", value: patientDetail.mrn };
    }

    return { label: "ID", value: patientDetail?.patient_id };
  };

  const { label, value } = getPatientIdentifier();

  const pruneOldData = (dataset, currentTimestamp) => {
    if (!dataset || Object.keys(dataset).length === 0) return dataset;

    const cutoffTime = currentTimestamp - RETENTION_WINDOW;
    const pruned = {};

    Object.entries(dataset).forEach(([ts, value]) => {
      const normalizedTs =
        String(ts).length === 13 ? Number(ts) / 1000 : Number(ts);
      if (normalizedTs >= cutoffTime) {
        pruned[ts] = value;
      }
    });

    return pruned;
  };

  const normalizeToMillis = (ts) => {
    const n = Number(ts);
    if (!n || isNaN(n)) return null;

    if (String(n).length === 10) return n * 1000;
    if (String(n).length === 13) return n;

    return null;
  };

  const findNearestBefore = (series, refTs, maxGapSec) => {
    if (!series || series.length === 0) return [];

    const ref =
      String(refTs).length === 13 ? Number(refTs) / 1000 : Number(refTs);

    for (let i = series.length - 1; i >= 0; i--) {
      const ts =
        String(series[i][0]).length === 13
          ? Number(series[i][0]) / 1000
          : Number(series[i][0]);

      const gap = ref - ts;

      if (gap < 0) continue;
      if (gap > maxGapSec) return [];

      return [series[i]];
    }

    return [];
  };

  // useEffect(() => {
  //   let hiddenAt = null;

  //   const onVisibility = () => {
  //     if (document.hidden) {
  //       hiddenAt = Date.now();
  //     } else {
  //       if (hiddenAt) {
  //         const awayFor = (Date.now() - hiddenAt) / 1000;

  //         if (awayFor > 10) {
  //           console.warn(`[${patientID}] User away ${awayFor}s → refreshing`);
  //           window.location.reload();
  //         }
  //       }
  //       hiddenAt = null;
  //     }
  //   };

  //   document.addEventListener("visibilitychange", onVisibility);

  //   return () => document.removeEventListener("visibilitychange", onVisibility);
  // }, [patientID]);

  useEffect(() => {
    if (!patientID) return;

    loadFetalMarkers(patientID).then((saved) => {
      fetalMarkersRef.current = saved;
    });
  }, [patientID]);

  useEffect(() => {
    if (!patientID) return;

    sessionAnchorRef.current = null;
    lastAnimatedTimestampRef.current[patientID] = 0;
    cacheLoadedRef.current = false;
    cacheCheckedRef.current = false;
    historyFetchedRef.current = false;

    setStartTime("");
    setSessionTime("");
  }, [patientID]);

  useEffect(() => {
    if (!isBeingCleaned) return;

    console.log(`[${patientID}] 🔥 HARD RESET session & monitoring`);

    sessionBlockedRef.current = true;
    sessionAnchorRef.current = null;
    lastAnimatedTimestampRef.current[patientID] = 0;

    setStartTime("");
    setSessionTime("");

    cacheLoadedRef.current = false;
    cacheCheckedRef.current = false;
    historyFetchedRef.current = false;

    try {
      localStorage.setItem(
        `patient_hard_cleaned_${patientID}`,
        String(Date.now()),
      );
    } catch (e) { }

    deleteFromIndexedDB(patientID).catch((e) => { });
    deleteFetalMarkers(patientID).catch(() => { });
  }, [isBeingCleaned, patientID]);

  // useEffect(() => {
  //   console.log(
  //     `[${patientID}] COMPONENT MOUNTED at`,
  //     new Date().toISOString(),
  //   );
  // }, []);

  useEffect(() => {
    isMountedRef.current = true;
    currentPatientRef.current = patientID;
    historyFetchedRef.current = false;
    cacheLoadedRef.current = false;
    // console.log(`[${patientID}] ChartLayout2 mounted`);

    return () => {
      // console.log(`[${patientID}] ChartLayout2 unmounting`);
      isMountedRef.current = false;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }

      if (saveDebounceRef.current) {
        clearTimeout(saveDebounceRef.current);
        saveDebounceRef.current = null;
      }

      historyFetchedRef.current = false;
      cacheLoadedRef.current = false;
      // cacheCheckedRef.current = false;
    };
  }, [patientID]);

  useEffect(() => {
    // console.log(`[${patientID}] Patient changed - resetting state`);

    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setKeyarData({});
    setKeyar({});
    cacheLoadedRef.current = false;
    historyFetchedRef.current = false;
    currentPatientRef.current = patientID;
    if (!lastAnimatedTimestampRef.current[patientID]) {
      lastAnimatedTimestampRef.current[patientID] = 0;
    }
  }, [patientID]);

  useEffect(() => {
    if (cookieUserData) {
      const parsedData = JSON.parse(cookieUserData);
      setAccountType(parsedData.account_type);
      setInterpretationsDisable(parsedData.fetal_distress_algorithm);
    }
  }, [cookieUserData]);

  useEffect(() => {
    if (cookieData) {
      setTheme(cookieData);
    }
  }, [cookieData]);

  //   useEffect(() => {
  //   // patient changed → full reset
  //   setKeyarData({});
  //   historyFetchedRef.current = false;

  //   if (animationIntervalRef.current) {
  //     clearInterval(animationIntervalRef.current);
  //     animationIntervalRef.current = null;
  //   }

  // }, [resetSignal]);

  useEffect(() => {
    if (patientBasicDetail && Object.keys(patientBasicDetail).length > 0) {
      // console.log("Patient Basic Detail received:", patientBasicDetail);

      setPatientDetail({
        name: patientBasicDetail.name || "-",
        age: patientBasicDetail.age || "-",
        patient_id: patientBasicDetail.id || "-",
        mrn: patientBasicDetail.mrn || "-",

        ges_period:
          patientBasicDetail.gestation_period ||
          patientBasicDetail.ges_period ||
          "-",
        monitoring_start_time: patientBasicDetail.monitoring_start_time || "-",
        dob: patientBasicDetail.dob || "-",
      });
    }
  }, [patientBasicDetail]);

  useEffect(() => {
    if (!keyar_data || keyar_data.length === 0) return;
    if (!sessionBlockedRef.current) return;

    const fhrParam = keyar_data.find((p) => p.param_type === "fhr");
    const timestamps = Object.keys(fhrParam?.latest_data || {});
    if (timestamps.length === 0) return;

    const firstTs = Math.min(
      ...timestamps.map((ts) =>
        String(ts).length === 13 ? Number(ts) / 1000 : Number(ts),
      ),
    );

    // console.log(
    //   `[${patientID}] 🟢 First LIVE data at`,
    //   new Date(firstTs * 1000).toISOString(),
    //   "→ unlocking session",
    // );

    sessionBlockedRef.current = false;

    sessionAnchorRef.current = firstTs;
    setStartTime(firstTs);
    setSessionTime(firstTs * 1000);
  }, [keyar_data, patientID]);

  useEffect(() => {
    if (!keyar_data || keyar_data.length === 0) return;

    const hardCleanTs = localStorage.getItem(
      `patient_hard_cleaned_${patientID}`,
    );

    if (hardCleanTs) {
      // console.log(
      //   `[${patientID}] 🟢 First live data received — clearing hard-clean flag`,
      // );
      localStorage.removeItem(`patient_hard_cleaned_${patientID}`);
    }
  }, [keyar_data, patientID]);

  useEffect(() => {
    if (!patientID || !keyar_data) return;

    let changed = false;

    const fmParam = keyar_data.find(
      (p) => p.param_type === "fetal_movement" || p.param_type === "fmm",
    );

    const values = fmParam?.latest_data || fmParam?.values || {};

    Object.entries(values).forEach(([ts, val]) => {
      const ms = normalizeToMillis(ts);
      if (!ms) return;

      if (Number(val) === 0 && !fetalMarkersRef.current.manual[ms]) {
        fetalMarkersRef.current.manual[ms] = 205;
        changed = true;
      }

      if (Number(val) === 1 && !fetalMarkersRef.current.automated[ms]) {
        fetalMarkersRef.current.automated[ms] = 205;
        changed = true;
      }
    });

    const fhrParam = keyar_data.find((p) => p.param_type === "fhr");
    const legacy = fhrParam?.meta_data?.fetal_movement;

    if (Array.isArray(legacy)) {
      legacy.forEach((item) => {
        if (typeof item === "object") {
          const ms = normalizeToMillis(item.timestamp);
          if (!ms) return;

          if (item.type === "automated") {
            if (!fetalMarkersRef.current.automated[ms]) {
              fetalMarkersRef.current.automated[ms] = 205;
              changed = true;
            }
          } else {
            if (!fetalMarkersRef.current.manual[ms]) {
              fetalMarkersRef.current.manual[ms] = 205;
              changed = true;
            }
          }
        } else {
          const ms = normalizeToMillis(item);
          if (ms && !fetalMarkersRef.current.manual[ms]) {
            fetalMarkersRef.current.manual[ms] = 205;
            changed = true;
          }
        }
      });
    }

    if (changed) {
      saveFetalMarkers(patientID, fetalMarkersRef.current);
    }
  }, [keyar_data, patientID]);

  useEffect(() => {
    if (isWsLive) return;
    if (!session_time) return;

    if (sessionBlockedRef.current) {
      console.warn(
        `[${patientID}] ⛔ session_time ignored (waiting for first live data)`,
      );
      return;
    }

    if (!cacheCheckedRef.current) return;

    const sessionSec =
      String(session_time).length === 13
        ? Math.floor(session_time / 1000)
        : session_time;

    // Same session already anchored — nothing to do.
    if (sessionAnchorRef.current === sessionSec) return;

    // A different, already-anchored session means backend started a new
    // monitoring session for this same patientID without session_time ever
    // going falsy in between (the only other reset path, below, requires a
    // 15s falsy gap) — so this is the only place that notices a session
    // restart. Without this, the chart (and its IndexedDB cache) would
    // stay pinned to the old session's anchor/data forever, even though
    // session_time itself has already moved on.
    if (sessionAnchorRef.current !== null) {
      console.log(
        `[${patientID}] 🔄 New session detected (${sessionAnchorRef.current} → ${sessionSec}) — resetting chart state`,
      );
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
      lastAnimatedTimestampRef.current[patientID] = 0;
      historyFetchedRef.current = false;
      cacheLoadedRef.current = false;
      setKeyarData({});
      setKeyar({});
      deleteFromIndexedDB(patientID).catch(() => {});
    }

    sessionAnchorRef.current = sessionSec;
    setStartTime(sessionSec);
    setSessionTime(sessionSec * 1000);

    console.log(
      `[${patientID}] ⏱️ Session anchor set`,
      new Date(sessionSec * 1000).toISOString(),
    );
  }, [session_time, patientID, cacheReady]);

  useEffect(() => {
    if (!patientID) return;

    const CLEAR_DELAY = 15000;

    const doClear = () => {
      try {
        console.log(
          `[${patientID}] Session ended — clearing cached session and IndexedDB`,
        );

        sessionAnchorRef.current = null;
        lastAnimatedTimestampRef.current[patientID] = 0;
        cacheLoadedRef.current = false;
        cacheCheckedRef.current = false;
        historyFetchedRef.current = false;

        setStartTime("");
        setSessionTime("");
        setKeyarData({});

        try {
          localStorage.setItem(
            `patient_hard_cleaned_${patientID}`,
            String(Date.now()),
          );
        } catch (e) { }

        deleteFromIndexedDB(patientID).catch((e) => { });
      } catch (e) {
        console.warn(`[${patientID}] Error clearing session cache`, e);
      }
    };

    if (isBeingCleaned) {
      if (sessionEndTimeoutRef.current) {
        clearTimeout(sessionEndTimeoutRef.current);
        sessionEndTimeoutRef.current = null;
      }
      doClear();
      return;
    }

    if (!session_time) {
      if (sessionEndTimeoutRef.current) {
        clearTimeout(sessionEndTimeoutRef.current);
      }
      sessionEndTimeoutRef.current = setTimeout(() => {
        sessionEndTimeoutRef.current = null;
        doClear();
      }, CLEAR_DELAY);
    } else {
      if (sessionEndTimeoutRef.current) {
        clearTimeout(sessionEndTimeoutRef.current);
        sessionEndTimeoutRef.current = null;
      }
    }

    return () => {
      if (sessionEndTimeoutRef.current) {
        clearTimeout(sessionEndTimeoutRef.current);
        sessionEndTimeoutRef.current = null;
      }
    };
  }, [session_time, patientID, isBeingCleaned]);

  useEffect(() => {
    if (isWsLive) return;
    if (isBeingCleaned) {
      console.log(`[${patientID}] ⛔ Skipping cache restore (being cleaned)`);
      return;
    }
    if (!patientID) return;

    let cancelled = false;

    const loadCachedData = async () => {
      try {
        let sessionStart = sessionAnchorRef.current;

        if (!sessionStart && session_time) {
          sessionStart =
            String(session_time).length === 13
              ? Math.floor(session_time / 1000)
              : session_time;
        }

        const hardCleanTs = Number(
          localStorage.getItem(`patient_hard_cleaned_${patientID}`) || 0,
        );

        // Without a real session anchor, loadFromIndexedDB's session-match
        // check gets passed `undefined` and silently skips itself —
        // accepting a stale cache from a PREVIOUS session for this same
        // patientID unconditionally. Wait for the next run (once
        // session_time arrives) instead of validating against nothing.
        if (!sessionStart && !hardCleanTs) {
          return;
        }

        if (hardCleanTs) {
          console.warn(
            `[${patientID}] 🚫 HARD CLEAN — forcing NEW session (ignoring backend + cache)`,
          );

          sessionAnchorRef.current = null;
          lastAnimatedTimestampRef.current[patientID] = 0;

          setStartTime("");
          setSessionTime("");

          isNewSessionRef.current = true;
          cacheCheckedRef.current = true;
          setCacheReady(true);

          return;
        }

        try {
          const hardCleanTs = Number(
            localStorage.getItem(`patient_hard_cleaned_${patientID}`) || 0,
          );

          if (hardCleanTs) {
            console.warn(
              `[${patientID}] 🚫 HARD CLEAN detected — skipping IndexedDB restore`,
            );

            await deleteFromIndexedDB(patientID);

            isNewSessionRef.current = true;
            cacheCheckedRef.current = true;
            setCacheReady(true);
            return;
          }
        } catch (e) { }

        const cached = await loadFromIndexedDB(patientID, sessionStart);
        if (!cached) {
          isNewSessionRef.current = true;
        } else {
          isNewSessionRef.current = false;
        }

        if (cached?.sessionStartTime) {
          // console.log(
          //   `[${patientID}] INDEXEDDB session restored:`,
          //   cached.sessionStartTime,
          //   new Date(cached.sessionStartTime * 1000).toISOString()
          // );

          sessionAnchorRef.current = cached.sessionStartTime;
          setStartTime(cached.sessionStartTime);
          setSessionTime(cached.sessionStartTime * 1000);

          // console.log(
          //   `[${patientID}] ♻️ Restored session from IndexedDB:`,
          //   new Date(cached.sessionStartTime * 1000).toISOString()
          // );
        }

        if (cancelled || !isMountedRef.current) return;

        if (!cached) {
          // console.log(`[${patientID}] 📂 No cache found`);
          cacheCheckedRef.current = true;
          setCacheReady(true);
          return;
        }

        // console.log(`[${patientID}] 🔄 Rehydrating from IndexedDB`);

        try {
          const fhrParam = cached.params.find((p) => p.param_type === "fhr");
          const keys = Object.keys(fhrParam?.data_set || {});
          if (keys.length > 0) {
            const lastTs = Math.max(
              ...keys.map((k) =>
                String(k).length === 13 ? Number(k) / 1000 : Number(k),
              ),
            );
            lastAnimatedTimestampRef.current[patientID] = lastTs;
            // console.log(
            //   `[${patientID}] ⏱️ Restored last timestamp:`,
            //   lastTs,
            //   "Total points:",
            //   keys.length
            // );
          }
        } catch (e) {
          console.warn("IndexedDB timestamp restore failed", e);
        }

        setKeyarData({ params: cached.params });
        cacheLoadedRef.current = true;
        cacheCheckedRef.current = true;
        setCacheReady(true);
      } catch (error) {
        // console.error(`[${patientID}] IndexedDB load error:`, error);
        cacheCheckedRef.current = true;
        setCacheReady(true);
      }
    };

    loadCachedData();

    return () => {
      cancelled = true;
    };
  }, [patientID]);

  useEffect(() => {
    if (!keyar_data || keyar_data.length === 0) return;

    const findParam = (type) =>
      keyar_data.find((val) => val.param_type === type) || {};

    const ucParam = findParam("uc");
    const fhrParam = findParam("fhr");

    setDeviceID(ucParam.serial_number || "");

    const ucMetaData = ucParam.meta_data || {};
    if (Object.keys(ucMetaData).length > 0) {
      setKeyar((prev) => ({
        ...prev,
        realtimeContractions: {
          contraction: Math.floor(ucMetaData?.contractions || 0),
          duration: Math.floor(ucMetaData?.duration || 0),
        },
      }));
    }
  }, [keyar_data]);

  useEffect(() => {
    // console.log(
    //   `[ChartLayout2] patientID=${patientID}`,
    //   "patientBasicDetail =",
    //   patientBasicDetail
    // );
  }, [patientID, patientBasicDetail]);

  useEffect(() => {
    if (!patientID || historyFetchedRef.current) {
      return;
    }

    if (isWsLive) {
      // WS patients get history from IndexedDB only (see the WS effect).
      historyFetchedRef.current = true;
      return;
    }

    if (!isRealPatientId(patientID)) {
      // Unassigned/emergency session — patientID is actually a session_uuid,
      // this endpoint expects a real numeric patient ID and will 400/404.
      historyFetchedRef.current = true;
      return;
    }

    if (cacheLoadedRef.current && fetchTrigger === 0) {
      const cacheAge =
        Date.now() / 1000 - (lastAnimatedTimestampRef.current[patientID] || 0);

      if (cacheAge < 300) {
        console.log(
          `[${patientID}] 📂 Recent cache loaded, skipping API fetch`,
        );
        historyFetchedRef.current = true;
        return;
      } else {
        console.log(
          `[${patientID}] 🔄 Cache is old, fetching from API to merge`,
        );
      }
    }

    const fetchFullKeyarHistory = async () => {
      if (!isMountedRef.current || currentPatientRef.current !== patientID) {
        return;
      }

      historyFetchedRef.current = true;
      abortControllerRef.current = new AbortController();

      try {
        // console.log(`[${patientID}] 🔄 Fetching history from API...`);

        const res = await axios.get(
          `${urls. DEV_URL}v1/nurse/patients/?patient_ids=${patientID}&forms=keyar_data`,
          {
            headers: {
              "Request-ID": "allow|me",
              "Content-Type": "application/json",
              Authorization: `Bearer ${cookie}`,
            },
            signal: abortControllerRef.current.signal,
          },
        );
        // console.log(
        //   `%c[${patientID}] BACKEND API FULL RESPONSE`,
        //   "color: green; font-weight: bold;",
        //   res.data,
        // );

        if (!isMountedRef.current || currentPatientRef.current !== patientID) {
          // console.log(
          //   `[${patientID}] ❌ Component unmounted or patient changed`
          // );
          return;
        }

        if (
          res &&
          res.data &&
          Array.isArray(res.data.response) &&
          res.data.response[0]
        ) {
          const keyarHistory = res.data.response[0].keyar_data || [];
          if (keyarHistory.length > 0) {
            const latestSnapshot = keyarHistory[keyarHistory.length - 1];
            const snapshotParams =
              latestSnapshot.params ||
              latestSnapshot.keyarData ||
              latestSnapshot.keyar_data ||
              latestSnapshot;

            if (Array.isArray(snapshotParams) && snapshotParams.length > 0) {
              const initialParams = snapshotParams.map((p) => ({
                ...p,
                data_set: p.latest_data || p.data_set || {},
              }));

              // console.log(
              //   `[${patientID}] ✅ API FETCH: Seeded ${
              //     initialParams[0]?.data_set
              //       ? Object.keys(initialParams[0].data_set).length
              //       : 0
              //   } points`
              // );
              setKeyarData((prevData) => {
                let mergedParams = initialParams;
                if (prevData.params && prevData.params.length > 0) {
                  mergedParams = prevData.params.map((existingParam) => {
                    const newParam = initialParams.find(
                      (p) => p.param_type === existingParam.param_type,
                    );
                    if (!newParam) return existingParam;
                    return {
                      ...existingParam,
                      data_set: {
                        ...existingParam.data_set,
                        ...newParam.data_set,
                      },
                    };
                  });
                }

                // Update lastAnimatedTimestamp to the latest in history
                try {
                  const fhrParam = mergedParams.find(p => p.param_type === 'fhr');
                  if (fhrParam?.data_set) {
                    const keys = Object.keys(fhrParam.data_set);
                    if (keys.length > 0) {
                      const latestTs = Math.max(...keys.map(k =>
                        String(k).length === 13 ? Number(k) / 1000 : Number(k)
                      ));
                      if (latestTs > (lastAnimatedTimestampRef.current[patientID] || 0)) {
                        lastAnimatedTimestampRef.current[patientID] = latestTs;
                        // console.log(`[${patientID}] ⏱️ History fetch: updated lastAnimatedTimestamp to ${latestTs}`);
                      }
                    }
                  }
                } catch (e) { }

                return { params: mergedParams };
              });
            }
          }
        }
      } catch (e) {
        if (e.name === "AbortError" || e.code === "ERR_CANCELED") {
          return;
        }

        console.warn(`[${patientID}] ⚠️ Failed to fetch:`, e);
        historyFetchedRef.current = false;
      }
    };

    fetchFullKeyarHistory();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [patientID, cookie, fetchTrigger]);

  useEffect(() => {
    if (isWsLive) return;
    if (!cacheReady) {
      return;
    }
    if (!keyar_data || keyar_data.length === 0) return;

    const findParam = (type) =>
      keyar_data.find((val) => val.param_type === type) || {};

    let fhrValue, ucValue, mhrValue, fhr2Value, spo2Value;

    if (sortedDataRef.current.__resetAfterBurst) {
      // 🔥 AFTER BURST → USE ANIMATOR AS SOURCE
      fhrValue = Object.fromEntries(sortedDataRef.current.fhr || []);
      ucValue = Object.fromEntries(sortedDataRef.current.uc || []);
      mhrValue = Object.fromEntries(sortedDataRef.current.mhr || []);
      fhr2Value = Object.fromEntries(sortedDataRef.current.fhr2 || []);
      spo2Value = Object.fromEntries(sortedDataRef.current.spo2 || []);
    } else {
      // NORMAL MODE → use backend
      fhrValue = findParam("fhr").latest_data || {};
      mhrValue = findParam("mhr").latest_data || {};
      ucValue = findParam("uc").latest_data || {};
      fhr2Value = findParam("fhr_two").latest_data || {};
      spo2Value = findParam("spo2").latest_data || {};
    }

    // const mhrValue = findParam("mhr").latest_data || {};
    // const ucValue = findParam("uc").latest_data || {};
    // const fhr2Value = findParam("fhr_two").latest_data || {};
    // const spo2Value = findParam("spo2").latest_data || {};

    const sortedFHRData = Object.entries(fhrValue).sort(
      ([a], [b]) => parseInt(a) - parseInt(b),
    );
    const sortedMHRData = Object.entries(mhrValue).sort(
      ([a], [b]) => parseInt(a) - parseInt(b),
    );
    const sortedUCData = Object.entries(ucValue).sort(
      ([a], [b]) => parseInt(a) - parseInt(b),
    );
    const sortedFHR2Data = Object.entries(fhr2Value).sort(
      ([a], [b]) => parseInt(a) - parseInt(b),
    );
    const sortedSpo2Data = Object.entries(spo2Value).sort(
      ([a], [b]) => parseInt(a) - parseInt(b),
    );

    if (sortedFHRData.length === 0) return;

    sortedDataRef.current = {
      fhr: sortedFHRData,
      mhr: sortedMHRData,
      uc: sortedUCData,
      fhr2: sortedFHR2Data,
      spo2: sortedSpo2Data,
    };

    let currentIndex = 0;
    const lastTimestamp = lastAnimatedTimestampRef.current[patientID];

    if (lastTimestamp !== undefined) {
      currentIndex = sortedFHRData.findIndex(
        ([ts]) => parseInt(ts) > lastTimestamp,
      );

      if (currentIndex === -1) {
        currentIndex = sortedFHRData.length - timestampsPerUpdate;
        if (currentIndex < 0) currentIndex = 0;
      }
    }

    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }

    animationIntervalRef.current = setInterval(() => {
      // ===================== BURST GUARD START =====================
      const latestFhr = sortedDataRef.current?.fhr || [];

      // console.log("📡 TICK", {
      //   totalPoints: latestFhr.length,
      //   lastAnimated: lastAnimatedTimestampRef.current[patientID],
      // });

      if (latestFhr.length > 0) {
        const norm = (ts) =>
          String(ts).length === 13 ? Number(ts) / 1000 : Number(ts);

        const lastAnimated = lastAnimatedTimestampRef.current[patientID] || 0;
        const backendFhr =
          keyar_data?.find((p) => p.param_type === "fhr")?.latest_data || {};

        const backendTs = Object.keys(backendFhr).map((ts) => norm(ts));

        if (backendTs.length > 0) {
          const newest = Math.max(...backendTs);
          const gap = newest - lastAnimated;

          // console.log("📊 BURST CHECK (backend)", {
          //   newest: new Date(newest * 1000).toISOString(),
          //   lastAnimated: new Date(lastAnimated * 1000).toISOString(),
          //   gap,
          // });

          if (gap > 12 && !sortedDataRef.current.__resetAfterBurst) {
            // console.warn("🔥 BURST MODE → merging missed data");

            const missedFhr = {};
            const missedUc = {};
            const missedMhr = {};
            const missedFhr2 = {};
            const missedSpo2 = {};

            const collect = (type, store) => {
              const matchingParams = keyar_data.filter((p) => p.param_type === type);

              matchingParams.forEach((param) => {
                const src = param.latest_data || {};
                Object.entries(src).forEach(([ts, v]) => {
                  const t = norm(ts);
                  if (t > lastAnimated && t <= newest) {
                    store[ts] = v;
                  }
                });
              });
            };

            collect("fhr", missedFhr);
            collect("uc", missedUc);
            collect("mhr", missedMhr);

            const sourceLen = keyar_data?.find(p => p.param_type === 'fhr')?.latest_data
              ? Object.keys(keyar_data.find(p => p.param_type === 'fhr').latest_data).length
              : 0;
            // console.log(`🔥 BURST DEBUG: Source 'keyar_data' has ${sourceLen} points. Gap detected.`);
            collect("fhr_two", missedFhr2);
            collect("spo2", missedSpo2);


            setKeyarData((prev) => {
              if (!prev.params) return prev;

              const merged = prev.params.map((p) => {
                let chunk = {};

                if (p.param_type === "fhr") chunk = missedFhr;
                if (p.param_type === "uc") chunk = missedUc;
                if (p.param_type === "mhr") chunk = missedMhr;
                if (p.param_type === "fhr_two") chunk = missedFhr2;
                if (p.param_type === "spo2") chunk = missedSpo2;

                return {
                  ...p,
                  data_set: {
                    ...p.data_set,
                    ...chunk,
                  },
                };
              });

              return { params: merged };
            });

            // const getLastVal = (obj) => {
            //   const keys = Object.keys(obj);
            //   if (keys.length === 0) return 0;
            //   const lastKey = keys.reduce((a, b) => (Number(a) > Number(b) ? a : b));
            //   return Math.floor(Number(obj[lastKey])) || 0;
            // };

            // const firstKey = Object.keys(missedFhr)[0];
            // const lastKey = Object.keys(missedFhr)[Object.keys(missedFhr).length - 1];
            // // console.log(`🔥 BURST MERGE DEBUG: Merging range ${firstKey} to ${lastKey}`);

            // setKeyar((prev) => ({
            //   ...prev,

            //   fhr: getLastVal(missedFhr) || prev.fhr,
            //   uc: getLastVal(missedUc) || prev.uc,
            //   mhr: getLastVal(missedMhr) || prev.mhr,
            //   fhr2: getLastVal(missedFhr2) || prev.fhr2,
            //   spo2: getLastVal(missedSpo2) || prev.spo2,

            //   fhrDataset: { ...prev.fhrDataset, ...missedFhr },
            //   ucDataset: { ...prev.ucDataset, ...missedUc },
            //   mhrDataset: { ...prev.mhrDataset, ...missedMhr },
            //   fhr2Dataset: { ...prev.fhr2Dataset, ...missedFhr2 },
            //   spo2Dataset: { ...prev.spo2Dataset, ...missedSpo2 },
            // }));


            const append = (series, chunk) => {
              const map = new Map(series);
              Object.entries(chunk).forEach(([ts, val]) => {
                map.set(ts, val);
              });
              return Array.from(map.entries()).sort(
                (a, b) => Number(a[0]) - Number(b[0]),
              );
            };

            sortedDataRef.current.fhr = append(
              sortedDataRef.current.fhr || [],
              missedFhr,
            );

            sortedDataRef.current.uc = append(
              sortedDataRef.current.uc || [],
              missedUc,
            );

            sortedDataRef.current.mhr = append(
              sortedDataRef.current.mhr || [],
              missedMhr,
            );

            // ─── 5) Move pointer ───
            const collectedCount = Object.keys(missedFhr).length;
            // console.log(`🔥 BURST MERGE: collected ${collectedCount} points from backend`);

            if (collectedCount > 0) {
              const firstKey = Object.keys(missedFhr)[0];
              const normFirst = String(firstKey).length === 13 ? Number(firstKey) / 1000 : Number(firstKey);
              const headGap = normFirst - lastAnimatedTimestampRef.current[patientID];

              if (headGap > 5) {
                // console.warn(`⚠️ BURST PARTIAL: Head gap of ${headGap.toFixed(1)}s detected. Fetching full history...`);
                historyFetchedRef.current = false; // Allow fetch
                setFetchTrigger(prev => prev + 1); // Trigger useEffect
              }

              lastAnimatedTimestampRef.current[patientID] = newest;
              lastNewDataTimeRef.current[patientID] = Date.now();
              sortedDataRef.current.__resetAfterBurst = true;
              sortedDataRef.current.__burstBase = newest;
            } else {
              console.warn("⚠️ BURST MERGE: No points collected despite gap? Skipping fast-forward.");
            }


            return;
          }
        }
      }
      if (!lastNewDataTimeRef.current[patientID]) {
        lastNewDataTimeRef.current[patientID] = Date.now();
      }

      if (!isMountedRef.current || currentPatientRef.current !== patientID) {
        if (animationIntervalRef.current) {
          clearInterval(animationIntervalRef.current);
          animationIntervalRef.current = null;
        }
        return;
      }

      const findParam = (type) =>
        keyar_data.find((val) => val.param_type === type) || {};

      const fhrValue = findParam("fhr").latest_data || {};
      const mhrValue = findParam("mhr").latest_data || {};
      const ucValue = findParam("uc").latest_data || {};
      const fhr2Value = findParam("fhr_two").latest_data || {};
      const spo2Value = findParam("spo2").latest_data || {};

      // const freshSortedFHR = Object.entries(fhrValue).sort(([a], [b]) => {
      //   const tsA = String(a).length === 13 ? Number(a) / 1000 : Number(a);
      //   const tsB = String(b).length === 13 ? Number(b) / 1000 : Number(b);
      //   return tsA - tsB;
      // });
      // const freshSortedMHR = Object.entries(mhrValue).sort(([a], [b]) => {
      //   const tsA = String(a).length === 13 ? Number(a) / 1000 : Number(a);
      //   const tsB = String(b).length === 13 ? Number(b) / 1000 : Number(b);
      //   return tsA - tsB;
      // });
      // const freshSortedUC = Object.entries(ucValue).sort(([a], [b]) => {
      //   const tsA = String(a).length === 13 ? Number(a) / 1000 : Number(a);
      //   const tsB = String(b).length === 13 ? Number(b) / 1000 : Number(b);
      //   return tsA - tsB;
      // });
      // const freshSortedFHR2 = Object.entries(fhr2Value).sort(([a], [b]) => {
      //   const tsA = String(a).length === 13 ? Number(a) / 1000 : Number(a);
      //   const tsB = String(b).length === 13 ? Number(b) / 1000 : Number(b);
      //   return tsA - tsB;
      // });
      // const freshSortedSpo2 = Object.entries(spo2Value).sort(([a], [b]) => {
      //   const tsA = String(a).length === 13 ? Number(a) / 1000 : Number(a);
      //   const tsB = String(b).length === 13 ? Number(b) / 1000 : Number(b);
      //   return tsA - tsB;
      // });

      if (!sortedDataRef.current.__resetAfterBurst) {
        // NORMAL MODE → build from backend
        sortedDataRef.current = {
          fhr: Object.entries(fhrValue).sort(
            (a, b) => Number(a[0]) - Number(b[0]),
          ),
          mhr: Object.entries(mhrValue).sort(
            (a, b) => Number(a[0]) - Number(b[0]),
          ),
          uc: Object.entries(ucValue).sort(
            (a, b) => Number(a[0]) - Number(b[0]),
          ),
          fhr2: Object.entries(fhr2Value).sort(
            (a, b) => Number(a[0]) - Number(b[0]),
          ),
          spo2: Object.entries(spo2Value).sort(
            (a, b) => Number(a[0]) - Number(b[0]),
          ),
        };
      } else {
        // 🔥 AFTER BURST → DO NOT REBUILD FROM BACKEND
        // console.log("🧠 Using MERGED timeline – skipping rebuild");

        // REMOVED: Reverting to normal mode causes buffer wipe if backend props are truncated.
        // Stay in merged mode to protect burst data.
        /* if (
          lastAnimatedTimestampRef.current[patientID] >=
          sortedDataRef.current.__burstBase
        ) {
          sortedDataRef.current.__resetAfterBurst = false;
        } */

        // 👉 MOST IMPORTANT:
        // Append only NEW backend points after 'newest'
        const newest = lastAnimatedTimestampRef.current[patientID];

        const appendOnly = (series, incoming) => {
          const norm = (ts) =>
            String(ts).length === 13 ? Number(ts) / 1000 : Number(ts);

          const extra = Object.entries(incoming).filter(
            ([ts]) => norm(ts) > newest,
          );

          // Merge and prune to prevent infinite growth (keep last 20000 points ~1.5h)
          return [...series, ...extra]
            .sort((a, b) => Number(a[0]) - Number(b[0]))
            .slice(-20000);
        };

        sortedDataRef.current.fhr = appendOnly(
          sortedDataRef.current.fhr,
          fhrValue,
        );

        sortedDataRef.current.uc = appendOnly(
          sortedDataRef.current.uc,
          ucValue,
        );

        sortedDataRef.current.mhr = appendOnly(
          sortedDataRef.current.mhr,
          mhrValue,
        );

        sortedDataRef.current.fhr2 = appendOnly(
          sortedDataRef.current.fhr2,
          fhr2Value,
        );

        sortedDataRef.current.spo2 = appendOnly(
          sortedDataRef.current.spo2,
          spo2Value,
        );
      }

      const latestSorted = sortedDataRef.current;
      if (!latestSorted.fhr || latestSorted.fhr.length === 0) return;

      const lastTimestamp = lastAnimatedTimestampRef.current[patientID] || 0;
      let startIndex = latestSorted.fhr.findIndex(([ts]) => {
        const normalizedTs =
          String(ts).length === 13 ? Number(ts) / 1000 : Number(ts);
        return normalizedTs > lastTimestamp;
      });

      if (startIndex === -1) {
        const now = Date.now();
        const lastSeen = lastNewDataTimeRef.current[patientID] || now;
        const staleness = (now - lastSeen) / 1000;

        // If no new data for more than 7 seconds, clear scalars and critical state
        if (staleness > 7) {
          const hasLiveValue = keyar.fhr !== 0 && keyar.fhr !== undefined;
          if (isCritical || hasLiveValue) {
            // console.warn(`[${patientID}] Data STALE (${staleness.toFixed(1)}s) - clearing live display`);
            onCriticalChange?.(false, []);
            setKeyar((prev) => ({
              ...prev,
              fhr: 0,
              fhr2: 0,
              mhr: 0,
              uc: 0,
              spo2: 0,
            }));
          }
        }
        return;
      }

      // We have new points! Update the timer
      lastNewDataTimeRef.current[patientID] = Date.now();

      const fhrTs = latestSorted.fhr[startIndex][0];

      const fhrSlice = [latestSorted.fhr[startIndex]];
      const mhrSlice = findNearestBefore(latestSorted.mhr, fhrTs, MAX_HOLD.mhr);
      const ucSlice = findNearestBefore(latestSorted.uc, fhrTs, MAX_HOLD.uc);
      const fhr2Slice = findNearestBefore(
        latestSorted.fhr2,
        fhrTs,
        MAX_HOLD.fhr2,
      );
      const spo2Slice = findNearestBefore(
        latestSorted.spo2,
        fhrTs,
        MAX_HOLD.spo2,
      );

      if (fhrSlice.length === 0) return;

      const lastFhrTs = fhrSlice[fhrSlice.length - 1][0];
      const normalizedFhrTs =
        String(lastFhrTs).length === 13
          ? Number(lastFhrTs)
          : Number(lastFhrTs) * 1000;

      const temp_delay = Date.now() - normalizedFhrTs;

      const delayInSecs = Math.floor(temp_delay / 1000);
      setIsDelayedByOneMin(delayInSecs > 60);

      const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${String(minutes).padStart(2, "0")} min ${String(
          remainingSeconds,
        ).padStart(2, "0")} sec`;
      };

      const convertArrayToObject = (array) => {
        const resultObject = {};
        array.forEach((item) => {
          const [key, value] = item;
          resultObject[key] = value;
        });
        return resultObject;
      };

      const fhrObject = convertArrayToObject(fhrSlice);
      const fhr2Object = convertArrayToObject(fhr2Slice);
      const mhrObject = convertArrayToObject(mhrSlice);
      const ucObject = convertArrayToObject(ucSlice);
      const spo2Object = convertArrayToObject(spo2Slice);

      const updatedKeyar = {
        fhr:
          fhrSlice.length > 0
            ? Math.floor(fhrSlice[fhrSlice.length - 1][1])
            : 0,
        mhr:
          mhrSlice.length > 0
            ? Math.floor(mhrSlice[mhrSlice.length - 1][1])
            : 0,
        uc: ucSlice.length > 0 ? Math.floor(ucSlice[ucSlice.length - 1][1]) : 0,
        fhr2:
          fhr2Slice.length > 0
            ? Math.floor(fhr2Slice[fhr2Slice.length - 1][1])
            : 0,
        spo2:
          spo2Slice.length > 0
            ? Math.floor(spo2Slice[spo2Slice.length - 1][1])
            : 0,
        delay: formatTime(delayInSecs),
        fhrDataset: fhrObject,
        fhr2Dataset: fhr2Object,
        mhrDataset: mhrObject,
        ucDataset: ucObject,
        spo2Dataset: spo2Object,
      };

      const alerts = [];
      const alertTs = normalizedFhrTs;

      if (alertTs <= lastAlertsSentRef.current.timestamp) {
        return; // 🚫 old data, do nothing
      }

      if (
        isValidNumber(updatedKeyar.fhr) &&
        (updatedKeyar.fhr > 160 || updatedKeyar.fhr < 110)
      ) {
        alerts.push({
          param_type: "FHR",
          value: updatedKeyar.fhr,
          timestamp: alertTs,
        });
      }

      if (
        isValidNumber(updatedKeyar.fhr2) &&
        (updatedKeyar.fhr2 > 160 || updatedKeyar.fhr2 < 110)
      ) {
        alerts.push({
          param_type: "FHR2",
          value: updatedKeyar.fhr2,
          timestamp: alertTs,
        });
      }

      if (
        isValidNumber(updatedKeyar.mhr) &&
        (updatedKeyar.mhr > 100 || updatedKeyar.mhr < 60)
      ) {
        alerts.push({
          param_type: "MHR",
          value: updatedKeyar.mhr,
          timestamp: alertTs,
        });
      }

      const areAlertsEqual = (a, b) => {
        if (a.length !== b.length) return false;
        return a.every((alert, i) =>
          alert.param_type === b[i].param_type &&
          alert.value === b[i].value &&
          alert.timestamp === b[i].timestamp
        );
      };

      const hasAlertChanged =
        alerts.length !== lastAlertsSentRef.current.alerts.length ||
        !areAlertsEqual(alerts, lastAlertsSentRef.current.alerts);

      if (hasAlertChanged) {
        onCriticalChange?.(alerts.length > 0, alerts);
        lastAlertsSentRef.current = { timestamp: alertTs, alerts: [...alerts] };
      }

      setKeyar((prev) => ({
        ...prev,
        ...updatedKeyar,
        fhrDataset: { ...prev.fhrDataset, ...updatedKeyar.fhrDataset },
        mhrDataset: { ...prev.mhrDataset, ...updatedKeyar.mhrDataset },
        ucDataset: { ...prev.ucDataset, ...updatedKeyar.ucDataset },
        fhr2Dataset: { ...prev.fhr2Dataset, ...updatedKeyar.fhr2Dataset },
        spo2Dataset: { ...prev.spo2Dataset, ...updatedKeyar.spo2Dataset },
      }));

      setKeyarData((prevData) => {
        if (!prevData.params || prevData.params.length === 0) {
          if (!historyFetchedRef.current) {
            return prevData;
          }
          const initialParams = keyar_data.map((param) => ({
            ...param,
            data_set: param.latest_data || param.data_set || {},
          }));
          return { params: initialParams };
        }

        const currentTimestamp = Date.now() / 1000;

        const updatedParams = prevData.params.map((existingParam) => {
          const liveParam = keyar_data.find(
            (p) => p.param_type === existingParam.param_type,
          );
          if (!liveParam) return existingParam;

          let animatedChunk = {};
          if (liveParam.param_type === "fhr") animatedChunk = fhrObject;
          else if (liveParam.param_type === "mhr") animatedChunk = mhrObject;
          else if (liveParam.param_type === "uc") animatedChunk = ucObject;
          else if (liveParam.param_type === "fhr_two")
            animatedChunk = fhr2Object;
          else if (liveParam.param_type === "spo2") animatedChunk = spo2Object;

          const mergedData = {
            ...existingParam.data_set,
            ...animatedChunk,
          };

          const prunedData = pruneOldData(mergedData, currentTimestamp);

          return {
            ...existingParam,
            data_set: prunedData,
          };
        });

        return { params: updatedParams };
      });

      if (fhrSlice.length > 0) {
        const lastTs = fhrSlice[fhrSlice.length - 1][0];
        const normalizedLastTs =
          String(lastTs).length === 13 ? Number(lastTs) / 1000 : Number(lastTs);
        lastAnimatedTimestampRef.current[patientID] = normalizedLastTs;
      }
    }, updateInterval);

    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
    };
  }, [keyar_data, patientID]);

  // ---- WebSocket-sourced patients -------------------------------------
  // 1) Session identity + IndexedDB history. The cache load is async and
  //    only ever fills in *older* points underneath the live series, it
  //    never gates or delays the live plot.
  useEffect(() => {
    if (!isWsLive || !patientID || isBeingCleaned) return;

    const toSec = (t) => (String(t).length === 13 ? Math.floor(t / 1000) : Number(t));
    const liveFirst = session_time ? toSec(session_time) : null;

    // A different session_uuid means a new monitoring session for this
    // patient: the Redux slice already dropped the old live data, so drop
    // the cached history too.
    if (wsLastUuidRef.current && session_uuid && wsLastUuidRef.current !== session_uuid) {
      setKeyarData({});
      deleteFromIndexedDB(patientID).catch(() => {});
      sessionAnchorRef.current = null;
      wsHistoryPatientRef.current = null;
    }
    if (session_uuid) wsLastUuidRef.current = session_uuid;

    sessionBlockedRef.current = false;
    if (liveFirst && (!sessionAnchorRef.current || liveFirst < sessionAnchorRef.current)) {
      sessionAnchorRef.current = liveFirst;
      setStartTime(liveFirst);
      setSessionTime(liveFirst * 1000);
    }

    if (wsHistoryPatientRef.current === `${patientID}|${session_uuid || ""}`) return;
    wsHistoryPatientRef.current = `${patientID}|${session_uuid || ""}`;

    (async () => {
      try {
        const cached = await loadFromIndexedDB(patientID);
        if (!cached || !isMountedRef.current || currentPatientRef.current !== patientID) return;

        const cachedUuid = cached.params?.find((p) => p.session_uuid)?.session_uuid;
        const cachedFhr = cached.params?.find((p) => p.param_type === "fhr");
        const cachedTs = Object.keys(cachedFhr?.data_set || {}).map((k) => toSec(k));
        if (cachedTs.length === 0) return;
        const cachedLast = Math.max(...cachedTs);

        // Same session? Prefer the uuid; for caches saved without one, fall
        // back to "contiguous with where the live data begins".
        const sameSession =
          cachedUuid && session_uuid
            ? cachedUuid === session_uuid
            : liveFirst == null || Math.abs(liveFirst - cachedLast) < 600;
        if (!sameSession) {
          deleteFromIndexedDB(patientID).catch(() => {});
          return;
        }

        const cutoff = cachedLast - RETENTION_WINDOW;
        const params = cached.params.map((param) => {
          const data_set = {};
          Object.entries(param.data_set || {}).forEach(([ts, v]) => {
            if (toSec(ts) >= cutoff) data_set[ts] = v;
          });
          return { ...param, data_set };
        });

        const cachedStart = cached.sessionStartTime ? toSec(cached.sessionStartTime) : null;
        const start = [cachedStart, sessionAnchorRef.current, liveFirst]
          .filter(Boolean)
          .reduce((a, b) => Math.min(a, b), Infinity);
        if (Number.isFinite(start)) {
          sessionAnchorRef.current = start;
          setStartTime(start);
          setSessionTime(start * 1000);
        }
        setKeyarData({ params });
      } catch (e) {
        wsHistoryPatientRef.current = null;
      }
    })();
  }, [isWsLive, patientID, session_time, session_uuid, isBeingCleaned]);

  // 2) Persist the live window to IndexedDB (tagged with session_uuid so a
  //    reload can tell whether the cache belongs to the current session).
  useEffect(() => {
    if (!isWsLive || !patientID || isBeingCleaned) return;
    const id = setInterval(() => {
      const anchor = sessionAnchorRef.current;
      const live = keyarDataPropRef.current;
      if (!anchor || !live || live.length === 0) return;
      const params = live.map((p) => ({
        param_type: p.param_type,
        meta_data: p.meta_data,
        serial_number: p.serial_number,
        session_uuid: session_uuid || null,
        data_set: p.latest_data || {},
      }));
      saveToIndexedDB(patientID, { params }, anchor);
    }, 5000);
    return () => clearInterval(id);
  }, [isWsLive, patientID, isBeingCleaned, session_uuid]);

  // 3) Numbers, delay and alerts straight from the newest live points,
  //    refreshed when a new frame lands (newest fhr key changes — not on
  //    every store flush) and once a second.
  const newestFhrKey = isWsLive
    ? keyar_data?.find((p) => p.param_type === "fhr")?.newestKey
    : null;
  useEffect(() => {
    if (!isWsLive || !keyarDataPropRef.current || keyarDataPropRef.current.length === 0) return;

    const norm = (ts) => (String(ts).length === 13 ? Number(ts) / 1000 : Number(ts));
    // O(1) via the reducer's newestKey; falls back to a scan for data that
    // didn't come through it.
    const newestOf = (data, newestKey) => {
      if (newestKey !== undefined && data[newestKey] !== undefined) {
        return { t: norm(newestKey), k: newestKey };
      }
      let best = null;
      Object.keys(data).forEach((k) => {
        const t = norm(k);
        if (best === null || t > best.t) best = { t, k };
      });
      return best;
    };
    const valueNear = (type, refTs, maxGap) => {
      const param = keyarDataPropRef.current.find((p) => p.param_type === type);
      const data = param?.latest_data || {};
      const nk = param?.newestKey;
      if (nk !== undefined && data[nk] !== undefined) {
        const nt = norm(nk);
        if (nt <= refTs) return refTs - nt <= maxGap ? Math.floor(Number(data[nk])) : 0;
      }
      let best = null;
      Object.keys(data).forEach((k) => {
        const t = norm(k);
        if (t <= refTs && refTs - t <= maxGap && (best === null || t > best.t)) {
          best = { t, v: data[k] };
        }
      });
      return best ? Math.floor(Number(best.v)) : 0;
    };

    const tick = () => {
      const fhrParam = keyarDataPropRef.current.find((p) => p.param_type === "fhr");
      const fhrData_ = fhrParam?.latest_data || {};
      const newest = newestOf(fhrData_, fhrParam?.newestKey);
      if (!newest) return;

      const now = Date.now();
      if (newest.t !== wsLastNewestRef.current.ts) {
        wsLastNewestRef.current = { ts: newest.t, at: now };
      }
      const stale = (now - wsLastNewestRef.current.at) / 1000 > 7;

      // Sub-second precision: the 4 points in each frame are stamped up to
      // ~0.75s ahead of the send time, so the real lag is often < 1s.
      const delayExact = Math.max(0, now / 1000 - newest.t);
      const delayInSecs = Math.floor(delayExact);
      const mm = String(Math.floor(delayInSecs / 60)).padStart(2, "0");
      const ss = (Math.floor((delayExact % 60) * 10) / 10).toFixed(1).padStart(4, "0");
      setIsDelayedByOneMin(delayExact > 60);

      // Diagnostic for the "plot stops while the side numbers keep moving"
      // report: log when the plotted series trails the live data.
      const plotted = wsPlottedLastRef.current;
      if (
        plotted &&
        document.visibilityState !== "hidden" &&
        newest.t - plotted > 5 &&
        now - wsStallLoggedAtRef.current > 5000
      ) {
        wsStallLoggedAtRef.current = now;
        console.warn(`[${patientID}] [WS-PLOT-STALL] chart is behind live data`, {
          newestLive: new Date(newest.t * 1000).toISOString(),
          lastPlotted: new Date(plotted * 1000).toISOString(),
          behindSecs: Math.round(newest.t - plotted),
          liveFhrPoints: Object.keys(fhrData_).length,
          cachedFhrPoints: Object.keys(
            keyarDataRef.current?.params?.find((p) => p.param_type === "fhr")?.data_set || {},
          ).length,
        });
      }

      const updated = stale
        ? { fhr: 0, fhr2: 0, mhr: 0, uc: 0, spo2: 0 }
        : {
            fhr: Math.floor(Number(fhrData_[newest.k])),
            mhr: valueNear("mhr", newest.t, MAX_HOLD.mhr),
            uc: valueNear("uc", newest.t, MAX_HOLD.uc),
            fhr2: valueNear("fhr_two", newest.t, MAX_HOLD.fhr2),
            spo2: valueNear("spo2", newest.t, MAX_HOLD.spo2),
          };

      setKeyar((prev) => ({ ...prev, ...updated, delay: `${mm} min ${ss} sec` }));

      const alertTs = newest.t * 1000;
      const alerts = [];
      if (isValidNumber(updated.fhr) && (updated.fhr > 160 || updated.fhr < 110)) {
        alerts.push({ param_type: "FHR", value: updated.fhr, timestamp: alertTs });
      }
      if (isValidNumber(updated.fhr2) && (updated.fhr2 > 160 || updated.fhr2 < 110)) {
        alerts.push({ param_type: "FHR2", value: updated.fhr2, timestamp: alertTs });
      }
      if (isValidNumber(updated.mhr) && (updated.mhr > 100 || updated.mhr < 60)) {
        alerts.push({ param_type: "MHR", value: updated.mhr, timestamp: alertTs });
      }
      const prevAlerts = lastAlertsSentRef.current.alerts;
      const changed =
        alerts.length !== prevAlerts.length ||
        alerts.some(
          (a, i) =>
            a.param_type !== prevAlerts[i].param_type ||
            a.value !== prevAlerts[i].value,
        );
      if (changed) {
        onCriticalChange?.(alerts.length > 0, alerts);
        lastAlertsSentRef.current = { timestamp: alertTs, alerts: [...alerts] };
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isWsLive, newestFhrKey]);

  useEffect(() => {
    if (isBeingCleaned) return;
    if (!patientID || !keyarData?.params || !sessionTime) return;

    if (saveDebounceRef.current) {
      clearTimeout(saveDebounceRef.current);
    }

    saveDebounceRef.current = setTimeout(() => {
      saveToIndexedDB(patientID, keyarData, sessionAnchorRef.current);
    }, 2000);

    return () => {
      if (saveDebounceRef.current) {
        clearTimeout(saveDebounceRef.current);
      }
    };
  }, [keyarData, patientID, session_time]);

  useEffect(() => {
    if (Object.keys(keyar).length > 0) {
      let fhr_data = keyar.fhr;
      setfhrCritical(
        fhr_data && fhr_data !== 0 && (fhr_data > 160 || fhr_data < 110),
      );

      let fhr2_data = keyar.fhr2;
      setfhr2Critical(
        fhr2_data && fhr2_data !== 0 && (fhr2_data > 160 || fhr2_data < 110),
      );

      let mhr_data = keyar.mhr;
      setmhrCritical(
        mhr_data && mhr_data !== 0 && (mhr_data > 100 || mhr_data < 60),
      );
    }
  }, [keyar]);

  // DEBUG: Monitor dataset size
  useEffect(() => {
    if (keyar?.fhrDataset) {
      const size = Object.keys(keyar.fhrDataset).length;
      // console.log(`📈 DATASET DEBUG: fhrDataset size is now ${size}`);
    }
  }, [keyar]);

  const sortTimestamp = (data) => {
    return Object.fromEntries(Object.entries(data).sort((a, b) => a[0] - b[0]));
  };

  // Highcharts just draws a straight line between whatever two points
  // exist, no matter how far apart in time — misleading when that gap is
  // real missing data (a sparse initial REST snapshot on a fresh page
  // load, or a WS dropout mid-session), not an actual smooth trend. Breaks
  // the line at any gap wider than gapThresholdSecs instead of connecting
  // across it (relies on connectNulls: false on the series, already set).
  const insertGapBreaks = (points, gapThresholdSecs = 5) => {
    const withBreaks = [];
    points.forEach((point, index) => {
      if (index > 0) {
        const prevTimestamp = points[index - 1][0];
        if (point[0] - prevTimestamp > gapThresholdSecs) {
          withBreaks.push([prevTimestamp + 1, null]);
        }
      }
      withBreaks.push(point);
    });
    return withBreaks;
  };

  // Turning latest_data (thousands of points x 5 series) into chart arrays
  // is the most expensive thing a card does, and the component re-renders far
  // more often than the data changes (ticks, alerts, store flushes). So the
  // result is cached per input, and for live WS data rebuilt at most about
  // twice a second; while the tab is hidden nothing is rebuilt (the browser
  // can't paint it) and the first render after it is shown catches up at once.
  // Alerts/delay run off keyarDataPropRef, not these arrays, so they keep
  // working in the background.
  const SERIES_MIN_REBUILD_MS = 500;
  const buildSeries = () => {
  let fhrData = [];
  let mhrData = [];
  let ucData = [];
  let fhr2Data = [];
  let spo2Data = [];
  let monitoring_time = "--:--:--";

  if (keyarData?.params) {
    const params = keyarData.params;
    const liveOf = (type) =>
      keyar_data?.find((p) => p.param_type === type)?.latest_data || {};

    const fhr_data = {
      ...(params.find((val) => val.param_type === "fhr")?.data_set || {}),
      ...(isWsLive ? liveOf("fhr") : keyar.fhrDataset || {}), // WS: live points; REST: animated data
    };

    const mhr_data = {
      ...(params.find((val) => val.param_type === "mhr")?.data_set || {}),
      ...(isWsLive ? liveOf("mhr") : keyar.mhrDataset || {}),
    };

    const uc_data = {
      ...(params.find((val) => val.param_type === "uc")?.data_set || {}),
      ...(isWsLive ? liveOf("uc") : keyar.ucDataset || {}),
    };

    const fhr2_data = {
      ...(params.find((val) => val.param_type === "fhr_two")?.data_set || {}),
      ...(isWsLive ? liveOf("fhr_two") : keyar.fhr2Dataset || {}),
    };

    const spo2_data = {
      ...(params.find((val) => val.param_type === "spo2")?.data_set || {}),
      ...(isWsLive ? liveOf("spo2") : keyar.spo2Dataset || {}),
    };

    const fmm_data =
      params.find((val) => val.param_type === "fhr")?.meta_data || {};
    let fetalMarker = fmm_data?.fetal_movement || [];
    const isNewFormat =
      fetalMarker.length > 0 && typeof fetalMarker[0] === "object";

    let automatedFetalMarker = [];
    let manualFetalMarker = [];
    let legacyFmm = [];

    if (isNewFormat) {
      automatedFetalMarker = fetalMarker
        .filter((data) => data.type === "automated")
        .map((obj) => [parseInt(obj.timestamp) / 1000, 205]);
      manualFetalMarker = fetalMarker
        .filter((data) => data.type === "manual")
        .map((obj) => [parseInt(obj.timestamp) / 1000, 205]);
    } else {
      legacyFmm = fetalMarker.map((timestamp) => [
        parseInt(timestamp) / 1000,
        205,
      ]);
    }

    const combinedManualFmm = [...manualFetalMarker, ...legacyFmm];

    fhrData = insertGapBreaks(Object.entries(fhr_data)
      .sort(([a], [b]) => {
        const tsA = String(a).length === 13 ? Number(a) / 1000 : Number(a);
        const tsB = String(b).length === 13 ? Number(b) / 1000 : Number(b);
        return tsA - tsB;
      })
      .map(([key, value]) => [
        String(key).length === 13 ? Number(key) / 1000 : Number(key),
        value == 0 ? null : Number(value),
      ]));

    fhr2Data = insertGapBreaks(Object.entries(fhr2_data)
      .sort(([a], [b]) => {
        const tsA = String(a).length === 13 ? Number(a) / 1000 : Number(a);
        const tsB = String(b).length === 13 ? Number(b) / 1000 : Number(b);
        return tsA - tsB;
      })
      .map(([key, value]) => [
        String(key).length === 13 ? Number(key) / 1000 : Number(key),
        value == 0 ? null : Number(value),
      ]));

    mhrData = insertGapBreaks(Object.entries(mhr_data)
      .sort(([a], [b]) => {
        const tsA = String(a).length === 13 ? Number(a) / 1000 : Number(a);
        const tsB = String(b).length === 13 ? Number(b) / 1000 : Number(b);
        return tsA - tsB;
      })
      .map(([key, value]) => [
        String(key).length === 13 ? Number(key) / 1000 : Number(key),
        value == 0 ? null : Number(value),
      ]));

    ucData = insertGapBreaks(Object.entries(uc_data)
      .sort(([a], [b]) => {
        const tsA = String(a).length === 13 ? Number(a) / 1000 : Number(a);
        const tsB = String(b).length === 13 ? Number(b) / 1000 : Number(b);
        return tsA - tsB;
      })
      .map(([key, value]) => [
        String(key).length === 13 ? Number(key) / 1000 : Number(key),
        value == 0 ? null : Number(value),
      ]));

    spo2Data = insertGapBreaks(Object.entries(spo2_data)
      .sort(([a], [b]) => {
        const tsA = String(a).length === 13 ? Number(a) / 1000 : Number(a);
        const tsB = String(b).length === 13 ? Number(b) / 1000 : Number(b);
        return tsA - tsB;
      })
      .map(([key, value]) => [
        String(key).length === 13 ? Number(key) / 1000 : Number(key),
        value == 0 ? null : Number(value),
      ]));

    const anchor = sessionAnchorRef.current;

    if (anchor && fhrData.length > 0) {
      const start_time = anchor * 1000;

      const lastPoint = fhrData[fhrData.length - 1];

      if (Array.isArray(lastPoint) && lastPoint.length > 0) {
        const last_time = lastPoint[0] * 1000;
        const timeDifferenceMs = last_time - start_time;

        const hours = Math.floor(timeDifferenceMs / (1000 * 60 * 60));
        const minutes = Math.floor(
          (timeDifferenceMs % (1000 * 60 * 60)) / (1000 * 60),
        );
        const seconds = Math.floor((timeDifferenceMs % (1000 * 60)) / 1000);

        monitoring_time = `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
      }
    }
  } else if (keyar_data && keyar_data.length > 0) {
    const params = keyar_data;

    const fhr_data =
      params.find((val) => val.param_type === "fhr")?.latest_data || {};
    const mhr_data =
      params.find((val) => val.param_type === "mhr")?.latest_data || {};
    const uc_data =
      params.find((val) => val.param_type === "uc")?.latest_data || {};
    const fhr2_data =
      params.find((val) => val.param_type === "fhr_two")?.latest_data || {};
    const spo2_data =
      params.find((val) => val.param_type === "spo2")?.latest_data || {};
    const fmm_data =
      params.find((val) => val.param_type === "fhr")?.meta_data || {};

    const sortedfhrData = sortTimestamp(fhr_data);
    const sortedmhrData = sortTimestamp(mhr_data);
    const sortedUCData = sortTimestamp(uc_data);
    const sortedfhr2Data = sortTimestamp(fhr2_data);
    const sortedSpo2Data = sortTimestamp(spo2_data);

    fhrData = insertGapBreaks(Object.entries(sortedfhrData).map(([key, value]) => [
      String(key).length == "13" ? Number(key / 1000) : Number(key),
      value == 0 ? null : Number(value),
    ]));

    fhr2Data = insertGapBreaks(Object.entries(sortedfhr2Data).map(([key, value]) => [
      String(key).length == "13" ? Number(key / 1000) : Number(key),
      value == 0 ? null : Number(value),
    ]));

    mhrData = insertGapBreaks(Object.entries(sortedmhrData).map(([key, value]) => [
      String(key).length == "13" ? Number(key / 1000) : Number(key),
      value == 0 ? null : Number(value),
    ]));

    ucData = insertGapBreaks(Object.entries(sortedUCData).map(([key, value]) => [
      String(key).length == "13" ? Number(key / 1000) : Number(key),
      value == 0 ? null : Number(value),
    ]));

    spo2Data = insertGapBreaks(Object.entries(sortedSpo2Data).map(([key, value]) => [
      String(key).length == "13" ? Number(key / 1000) : Number(key),
      value == 0 ? null : Number(value),
    ]));

    const anchor = sessionAnchorRef.current;

    if (anchor && fhrData.length > 0) {
      const start_time = anchor * 1000;

      const lastPoint = fhrData[fhrData.length - 1];

      if (Array.isArray(lastPoint) && lastPoint.length > 0) {
        const last_time = lastPoint[0] * 1000;
        const timeDifferenceMs = last_time - start_time;

        const hours = Math.floor(timeDifferenceMs / (1000 * 60 * 60));
        const minutes = Math.floor(
          (timeDifferenceMs % (1000 * 60 * 60)) / (1000 * 60),
        );
        const seconds = Math.floor((timeDifferenceMs % (1000 * 60)) / 1000);

        monitoring_time = `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
      }
    }
  }

  return { fhrData, mhrData, ucData, fhr2Data, spo2Data, monitoring_time };
  };

  const seriesDeps = [
    keyarData,
    keyar_data,
    isWsLive,
    sessionAnchorRef.current,
    isWsLive ? null : keyar.fhrDataset,
    isWsLive ? null : keyar.mhrDataset,
    isWsLive ? null : keyar.ucDataset,
    isWsLive ? null : keyar.fhr2Dataset,
    isWsLive ? null : keyar.spo2Dataset,
  ];
  const seriesCache = seriesCacheRef.current;
  const depsUnchanged =
    seriesCache.deps !== null &&
    seriesCache.deps.length === seriesDeps.length &&
    seriesCache.deps.every((d, i) => d === seriesDeps[i]);
  const nowMs = Date.now();
  const reuseCache =
    seriesCache.value !== null &&
    (depsUnchanged ||
      !isTabVisible ||
      (isWsLive && nowMs - seriesCache.builtAt < SERIES_MIN_REBUILD_MS));
  if (!reuseCache) {
    seriesCacheRef.current = { deps: seriesDeps, value: buildSeries(), builtAt: nowMs };
  }
  const { fhrData, mhrData, ucData, fhr2Data, spo2Data, monitoring_time } =
    seriesCacheRef.current.value;

  wsPlottedLastRef.current =
    fhrData && fhrData.length > 0 ? fhrData[fhrData.length - 1][0] : null;

  useEffect(() => {
    try {
      if (fhrData && fhrData.length > 0) {
        const first = fhrData[0][0];
        const last = fhrData[fhrData.length - 1][0];
        // console.log(
        //   `GRAPH-PROP: patient ${patientID} fhrCount=${
        //     fhrData.length
        //   } earliest=${new Date(first * 1000).toISOString()} latest=${new Date(
        //     last * 1000
        //   ).toISOString()}`
        // );
      } else {
        // console.log(`GRAPH-PROP: patient ${patientID} fhrCount=0`);
      }

      if (mhrData && mhrData.length > 0) {
        const first = mhrData[0][0];
        const last = mhrData[mhrData.length - 1][0];
        // console.log(
        //   `GRAPH-PROP: patient ${patientID} mhrCount=${
        //     mhrData.length
        //   } earliest=${new Date(first * 1000).toISOString()} latest=${new Date(
        //     last * 1000
        //   ).toISOString()}`
        // );
      }

      if (ucData && ucData.length > 0) {
        const first = ucData[0][0];
        const last = ucData[ucData.length - 1][0];
        // console.log(
        //   `GRAPH-PROP: patient ${patientID} ucCount=${
        //     ucData.length
        //   } earliest=${new Date(first * 1000).toISOString()} latest=${new Date(
        //     last * 1000
        //   ).toISOString()}`
        // );
      }
    } catch (e) {
      console.warn("GRAPH-PROP debug error", e);
    }
  }, [fhrData, mhrData, ucData, patientID]);

  const fhrTimestamps = fhrData.map((p) => p[0]);
  const minFhrTs = Math.min(...fhrTimestamps);
  const maxFhrTs = Math.max(...fhrTimestamps);

  const manualFmm = Object.entries(fetalMarkersRef.current.manual)
    .map(([ts, y]) => [Number(ts) / 1000, y])
    .filter(([ts]) => ts >= minFhrTs && ts <= maxFhrTs);

  const autoFmm = Object.entries(fetalMarkersRef.current.automated)
    .map(([ts, y]) => [Number(ts) / 1000, y])
    .filter(([ts]) => ts >= minFhrTs && ts <= maxFhrTs);

  return (
    <CriticalPulseWrapper isCritical={isCritical} isSingleView={isSingleView} theme={theme}>
      <div className="chart-container-wrapper">
        <div className="chart-scale-box">
          <div
            className={`chart-content ${theme === "light-theme" ? "light" : "dark"
              }`}
          >
            {loading ? (
              <Loader />
            ) : (
              <div
                style={{
                  // background: theme === "light-theme" ? `#fff` : `#0E0E0E`,
                  width: "100%",
                  height: "100%",
                  minHeight: 0,
                  padding: 0,
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {isDelayedByOneMin && (
                  <div className="delayedByOneMin-div">
                    <img src="/warning.png" alt="" className="small-warning" />
                    <p className="delayedByOneMin-text">
                      Data loading is delayed by more than 1 min
                    </p>
                  </div>
                )}

                <div className="patient_detail">
                  <div className="graph_layout">
                    <div
                      className={`detail_card ${theme === "light-theme" ? "light-theme" : "dark-theme"
                        }`}
                    >
                      {!isSingleView && (
                        <div className="hospital-name-container">
                          <p className="name">
                            {isValidPatientName(patientDetail?.name) ? (
                              <>
                                {patientDetail.name}
                                {value ? ` (${value})` : ""}
                              </>
                            ) : (
                              <Tooltip title={session_uuid || ""} arrow placement="bottom-start">
                                <span>
                                  Session UUID: {getDisplayName(patientDetail?.name, session_uuid)}
                                  {session_uuid ? "..." : ""}
                                </span>
                              </Tooltip>
                            )}
                          </p>

                          {hospitalName && (
                            <div className="hospital-tooltip-wrapper">
                              <Tooltip
                                title={hospitalName}
                                placement="bottom-end"
                                arrow
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      backgroundColor:
                                        theme === "light-theme"
                                          ? "#FFFFFF"
                                          : "#2B2B2B",
                                      color:
                                        theme === "light-theme"
                                          ? "#000000"
                                          : "#FFFFFF",
                                      fontSize: "10px",
                                      padding: "4px 10px",
                                      borderRadius: "8px",
                                      maxWidth: "180px",
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                      lineHeight: 1.4,
                                    },
                                  },
                                  arrow: {
                                    sx: {
                                      color:
                                        theme === "light-theme"
                                          ? "#FFFFFF"
                                          : "#2B2B2B",
                                    },
                                  },
                                }}
                              >
                                <div className="hospital_name_container">
                                  <img
                                    src={"/hospital_icon.png"}
                                    alt="hospital icon"
                                    className="hospital-icon-img"
                                  />
                                  <p className="hospital-name">
                                    {hospitalName}
                                  </p>
                                </div>
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      )}

                      {isSingleView && (
                        <>
                          <p className="name">
                            {isValidPatientName(patientDetail?.name) ? (
                              <>
                                {patientDetail.name}
                                {value ? ` (${value})` : ""}
                              </>
                            ) : (
                              <Tooltip title={session_uuid || ""} arrow placement="bottom-start">
                                <span>
                                  Session UUID: {getDisplayName(patientDetail?.name, session_uuid)}
                                  {session_uuid ? "..." : ""}
                                </span>
                              </Tooltip>
                            )}
                          </p>
                          <p className="age">
                            Age: {patientDetail?.age || "--"}
                          </p>
                          <p className="device-id">
                            Device ID: {deviceID || "--"}
                          </p>
                          <p className="delayed-timer">
                            Delayed By: {keyar.delay || "--"}
                          </p>
                          <p className="device-id">
                            Monitoring Time: {monitoring_time}
                          </p>
                          <p className="device-id">
                            Session Started At:{" "}
                            {sessionTime &&
                              !isNaN(new Date(sessionTime).getTime())
                              ? getReadableTimeOnly(sessionTime)
                              : "--"}
                          </p>
                        </>
                      )}
                    </div>

                    <div className="chart-wrapper">
                      <Graph_new
                        patientID={patientID}
                        startTime={startTime}
                        fhrData={fhrData}
                        fhr2Data={fhr2Data}
                        ucData={ucData}
                        mhrData={mhrData}
                        fetalMarker={manualFmm}
                        automatedFetalMarker={autoFmm}
                        spo2Data={spo2Data}
                        accountType={accountType}
                        viewMode={isSingleView ? "single" : "grid"}
                        isCritical={isCritical}
                      />
                    </div>
                  </div>

                  <div
                    className={`detail_layout ${theme === "light-theme"
                      ? "detail_layout-light"
                      : "detail_layout-dark"
                      }`}
                  >
                    <div className="fhr-fhr2-mhr-card">
                      <div className="monitoring-data-layout">
                        <div>
                          {/* FHR Card */}
                          <div className="fhr-layout-card">
                            <div className="name-layout">
                              {/* <img
                                src={
                                  theme === "light-theme"
                                    ? "/fhr-yellow.png"
                                    : "/fhr-yellow.png"
                                }
                                alt="FHR Logo"
                                className="imageLogo fhr-light-icon"
                              /> */}

                              <p className="fhr" style={{ color: VITAL_LABEL_COLORS.fhr1 }}>FHR1</p>
                              {isSingleView && (
                                <div
                                  role="img"
                                  aria-label="FHR Logo"
                                  className="imageLogo fhr-light-icon"
                                  style={iconMaskStyle("/fhr-yellow1.png", VITAL_LABEL_COLORS.fhr1)}
                                />
                              )}
                            </div>
                            <div className="number-layout">
                              <p
                                className={
                                  fhrcritical ? "critical-number" : "number"
                                }
                              >
                                {!keyar?.fhr || keyar?.fhr == 0
                                  ? "--"
                                  : keyar?.fhr}
                              </p>
                              {isSingleView && <p className="bpm">BPM</p>}
                            </div>
                          </div>

                          {/* FHR2 Card */}
                          <div className="fhr-layout-card">
                            <div className="name-layout">
                              {/* <img
                                src={
                                  theme === "light-theme"
                                    ? "/fhr2-blue.png"
                                    : "/fhr2-blue.png"
                                }
                                alt="FHR2 Logo"
                                className="imageLogo fhr2-light-icon"
                              /> */}

                              <p className="fhr" style={{ color: VITAL_LABEL_COLORS.fhr2 }}>FHR2</p>
                              {isSingleView && (
                                <div
                                  role="img"
                                  aria-label="FHR2 Logo"
                                  className="imageLogo fhr2-light-icon"
                                  style={iconMaskStyle("/fhr2-blue1.png", VITAL_LABEL_COLORS.fhr2)}
                                />
                              )}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-evenly",
                                }}
                              ></div>
                            </div>
                            <div className="number-layout">
                              <p
                                className={
                                  fhr2critical ? "critical-number" : "number"
                                }
                              >
                                {!keyar?.fhr2 || keyar?.fhr2 == 0
                                  ? "--"
                                  : keyar?.fhr2}
                              </p>
                              {isSingleView && <p className="bpm">BPM</p>}
                            </div>
                          </div>

                          {/* MHR Card */}
                          <div className="fhr-layout-card">
                            <div className="name-layout">
                              {/* <img
                                src={
                                  theme === "light-theme"
                                    ? "/mhr_dark.svg"
                                    : "/mhr_dark.svg"
                                }
                                alt="MHR Logo"
                                className="imageLogo mhr-light-icon"
                                width={25}
                                height={25}
                              /> */}

                              <p className="fhr" style={{ color: VITAL_LABEL_COLORS.mhr }}>MHR</p>
                              {isSingleView && (
                                <div
                                  role="img"
                                  aria-label="MHR Logo"
                                  className="imageLogo mhr-light-icon"
                                  style={iconMaskStyle("/ua_pink1.svg", VITAL_LABEL_COLORS.mhr)}
                                />
                              )}
                            </div>
                            <div className="number-layout">
                              <p
                                className={
                                  mhrcritical ? "critical-number" : "number"
                                }
                              >
                                {!keyar?.mhr || keyar?.mhr == 0
                                  ? "--"
                                  : keyar?.mhr}
                              </p>
                              {isSingleView && <p className="bpm">BPM</p>}
                            </div>
                          </div>

                          {/* SpO2 Card */}
                          {isSingleView && (
                            <div className="fhr-layout-card">
                              <div className="name-layout">
                                <p className="fhr spo2-text" style={{ color: VITAL_LABEL_COLORS.spo2 }}>SpO₂</p>
                                {isSingleView && (
                                  <div
                                    role="img"
                                    aria-label="SpO2 Logo"
                                    className="imageLogo"
                                    style={{ ...iconMaskStyle("/spo2.svg", VITAL_LABEL_COLORS.spo2), width: 25, height: 25 }}
                                  />
                                )}
                              </div>
                              <div className="number-layout spo2-number-layout">
                                <p className="number">
                                  {!keyar?.spo2 || keyar?.spo2 == 0
                                    ? "--"
                                    : keyar?.spo2}
                                </p>
                                <p className="bpm">%</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="uc-toco-layout">
                      {isSingleView && (
                        <div className="uc-card">
                          <div className="uc-logo-layout">
                            {isSingleView && (
                              <img
                                src={
                                  theme === "light-theme"
                                    ? "/ua_pink1.svg"
                                    : "/ua_pink1.svg"
                                }
                                alt="toco Logo"
                                className="imageLogo"
                              />
                            )}

                            <p className="uc-text">UA</p>
                          </div>
                          <div className="uc-number-layout">
                            <div className="uc-number">
                              <p className="number">
                                {keyar?.realtimeContractions?.contraction ||
                                  "--"}
                              </p>
                              <p className="small-text">Contraction</p>
                              <p className="large-text">Total Count</p>
                            </div>
                            <div className="uc-number">
                              <p className="number">
                                {keyar?.realtimeContractions?.duration || "--"}
                              </p>
                              <p className="small-text">Seconds</p>
                              <p className="large-text">Avg. Duration</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="toco-card">
                        <div className="toco-name-layout">
                          {isSingleView && (
                            <div
                              role="img"
                              aria-label="TOCO Logo"
                              className="imageLogo"
                              style={iconMaskStyle(
                                theme === "light-theme" ? "/toco-light.svg" : "/toco.png",
                                VITAL_LABEL_COLORS.toco,
                              )}
                            />
                          )}
                          <p className="toco-text" style={{ color: VITAL_LABEL_COLORS.toco }}>TOCO</p>
                        </div>
                        <div className="toco-number-layout">
                          <p
                            className={
                              theme === "light-theme"
                                ? "toco-number-light"
                                : "toco-number"
                            }
                          >
                            {!keyar?.uc || keyar?.uc == 0 ? "--" : keyar?.uc}
                          </p>
                          {isSingleView && <p className="pressure">Pressure</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </CriticalPulseWrapper>
  );
};

const mapStateToProps = (state, ownProps) => ({
  keyar_data:
    ownProps && ownProps.keyar_data
      ? ownProps.keyar_data
      : state.keyarData.keyarData,
});

export default connect(mapStateToProps)(React.memo(ChartLayout2, (prevProps, nextProps) => {
  // Only re-render when actual data or critical props change
  return (
    prevProps.patientID === nextProps.patientID &&
    prevProps.keyar_data === nextProps.keyar_data &&
    prevProps.session_time === nextProps.session_time &&
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.isBeingCleaned === nextProps.isBeingCleaned
  );
}));
